import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Link } from "react-router-dom";
import { getAppointments } from "../../core/api/appointmentService";
import {
  getAppointmentReport,
  getSalesReport,
  getExpenseReport,
  getLowStockReport,
  getReceivables,
} from "../../core/api/reportService";
import { formatTime12 } from "../../core/utils/formatTime";

// ── date helpers ───────────────────────────────────────────────────────────────
const toYMD = (d) => d.toISOString().split("T")[0];
const TODAY = toYMD(new Date());

const weekStart = () => {
  const d = new Date();
  const diff = d.getDay() === 0 ? -6 : 1 - d.getDay(); // Monday
  d.setDate(d.getDate() + diff);
  return toYMD(d);
};
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

// Robust client-side date filter: compares YYYY-MM-DD prefix only
const inRange = (rawDate, from, to) => {
  if (!rawDate) return false;
  const d = String(rawDate).split("T")[0];
  return d >= from && d <= to;
};

const sumField = (arr, ...keys) =>
  arr.reduce((s, r) => {
    const val = keys.map((k) => r[k]).find((v) => v != null) ?? 0;
    return s + Number(val);
  }, 0);

const currency = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week",  label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom" },
];

const STATUS_COLOR = {
  Booked: "primary", Completed: "success", Cancelled: "danger",
  "In Progress": "warning", NoShow: "warning",
};

// ── StatCard ───────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, color, link }) => (
  <div className="col-xl-3 col-sm-6 col-12">
    <div className="card h-100">
      <div className="card-body">
        <div className="dash-widget-header">
          <span className={`dash-widget-icon bg-${color}`}>{icon}</span>
          <div className="dash-count">
            <div className="dash-title">{label}</div>
            <div className="dash-counts">
              <p>{value === null ? <span className="spinner-border spinner-border-sm" /> : value}</p>
            </div>
          </div>
        </div>
        {sub  && <small className="text-muted d-block mt-1">{sub}</small>}
        {link && <Link to={link} className="text-muted small d-block mt-1">View details →</Link>}
      </div>
    </div>
  </div>
);

// ── Dashboard ──────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const [period,     setPeriod]     = useState("today");
  const [customFrom, setCustomFrom] = useState(TODAY);
  const [customTo,   setCustomTo]   = useState(TODAY);
  const [showCustom, setShowCustom] = useState(false);

  // The range that is actually applied (custom only updates on Apply click)
  const [appliedFrom, setAppliedFrom] = useState(TODAY);
  const [appliedTo,   setAppliedTo]   = useState(TODAY);

  // Period-scoped stats (null = loading)
  const [apptCount,   setApptCount]   = useState(null);
  const [collection,  setCollection]  = useState(null);
  const [salesAmt,    setSalesAmt]    = useState(null);
  const [expenseAmt,  setExpenseAmt]  = useState(null);

  // Always-current (loaded once)
  const [receivables, setReceivables] = useState(null);
  const [todayAppts,  setTodayAppts]  = useState(null);
  const [lowStock,    setLowStock]    = useState(null);

  // Effective date range
  const getRange = () => {
    if (period === "today")  return { from: TODAY,       to: TODAY };
    if (period === "week")   return { from: weekStart(),  to: TODAY };
    if (period === "month")  return { from: monthStart(), to: TODAY };
    return { from: appliedFrom, to: appliedTo };
  };

  // Reload period stats whenever period or applied custom range changes
  useEffect(() => {
    const { from, to } = getRange();

    setApptCount(null); setCollection(null); setSalesAmt(null); setExpenseAmt(null);

    // Appointments — filter by appointmentDate client-side
    getAppointmentReport({ from, to })
      .then((r) => {
        const payload = r.data ?? {};
        const all = Array.isArray(payload) ? payload : (payload.records ?? []);
        const filtered = all.filter((a) =>
          inRange(a.appointmentDate ?? a.date, from, to)
        );
        setApptCount(filtered.length);
      })
      .catch(() => setApptCount(0));

    // Sales — filter by invoiceDate client-side; derive both collection and revenue
    getSalesReport({ from, to })
      .then((r) => {
        const payload = r.data ?? {};
        const all = Array.isArray(payload) ? payload : (payload.records ?? []);
        const filtered = all.filter((inv) =>
          inRange(inv.invoiceDate ?? inv.date, from, to)
        );
        setCollection(sumField(filtered, "paidAmount", "collectionAmount", "netTotal"));
        setSalesAmt(sumField(filtered, "netTotal", "totalAmount", "amount"));
      })
      .catch(() => { setCollection(0); setSalesAmt(0); });

    // Expenses — filter by expenseDate client-side
    getExpenseReport({ from, to })
      .then((r) => {
        const payload = r.data ?? {};
        const all = Array.isArray(payload) ? payload : (payload.records ?? []);
        const filtered = all.filter((e) =>
          inRange(e.expenseDate ?? e.date, from, to)
        );
        setExpenseAmt(sumField(filtered, "amount", "totalAmount"));
      })
      .catch(() => setExpenseAmt(0));

  }, [period, appliedFrom, appliedTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load static data once
  useEffect(() => {
    getAppointments({ date: TODAY })
      .then((r) => setTodayAppts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTodayAppts([]));

    getReceivables({ from: "2000-01-01", to: TODAY })
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : (r.data?.records ?? []);
        setReceivables(sumField(all, "balanceAmount", "balance", "pendingAmount"));
      })
      .catch(() => setReceivables(0));

    getLowStockReport({})
      .then((r) => setLowStock(Array.isArray(r.data) ? r.data : (r.data?.records ?? [])))
      .catch(() => setLowStock([]));
  }, []);

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    setAppliedFrom(customFrom);
    setAppliedTo(customTo);
    setPeriod("custom");
    setShowCustom(false);
  };

  const netProfit = collection != null && expenseAmt != null ? collection - expenseAmt : null;

  const periodLabel = {
    today:  "Today",
    week:   "This Week",
    month:  "This Month",
    custom: `${appliedFrom} – ${appliedTo}`,
  }[period];

  return (
    <div className="page-wrapper">
      <div className="content">

        {/* Header */}
        <div className="page-header flex-wrap gap-2">
          <div className="page-title">
            <h4>Clinic Dashboard</h4>
            <h6 className="text-muted mb-0">Overview — {periodLabel}</h6>
          </div>

          {/* Period tabs */}
          <div className="d-flex align-items-center flex-wrap gap-2">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                className={`btn btn-sm ${period === p.key ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => {
                  if (p.key === "custom") { setShowCustom((v) => !v); return; }
                  setShowCustom(false);
                  setPeriod(p.key);
                }}
              >
                {p.label}
              </button>
            ))}

            {showCustom && (
              <div className="d-flex align-items-center gap-2 ms-1 flex-wrap">
                <input
                  type="date" className="form-control form-control-sm" style={{ width: 145 }}
                  value={customFrom} max={customTo || TODAY}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
                <span className="text-muted small">to</span>
                <input
                  type="date" className="form-control form-control-sm" style={{ width: 145 }}
                  value={customTo} min={customFrom} max={TODAY}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
                <button className="btn btn-sm btn-success" onClick={applyCustom}>Apply</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowCustom(false)}>✕</button>
              </div>
            )}
          </div>
        </div>

        {/* Stat row 1 — period-scoped */}
        <div className="row g-3">
          <StatCard
            icon={<Icon.Calendar size={22} color="#fff" />}
            label="Appointments"
            value={apptCount}
            sub={`Period: ${periodLabel}`}
            color="1" link="/appointments"
          />
          <StatCard
            icon={<Icon.DollarSign size={22} color="#fff" />}
            label="Collection"
            value={collection != null ? currency(collection) : null}
            sub={`Period: ${periodLabel}`}
            color="2" link="/reports/sales"
          />
          <StatCard
            icon={<Icon.FileText size={22} color="#fff" />}
            label="Sales Revenue"
            value={salesAmt != null ? currency(salesAmt) : null}
            sub={`Period: ${periodLabel}`}
            color="3" link="/invoices"
          />
          <StatCard
            icon={<Icon.TrendingDown size={22} color="#fff" />}
            label="Expenses"
            value={expenseAmt != null ? currency(expenseAmt) : null}
            sub={`Period: ${periodLabel}`}
            color="4" link="/expenses"
          />
        </div>

        {/* Stat row 2 — derived + always-current */}
        <div className="row g-3 mt-0">
          <StatCard
            icon={<Icon.TrendingUp size={22} color="#fff" />}
            label="Net Profit"
            value={netProfit != null ? currency(netProfit) : null}
            sub="Collection − Expenses"
            color="2" link="/reports/mis"
          />
          <StatCard
            icon={<Icon.AlertCircle size={22} color="#fff" />}
            label="Pending Receivables"
            value={receivables != null ? currency(receivables) : null}
            sub="All-time outstanding"
            color="4" link="/reports/receivables"
          />
        </div>

        {/* Tables */}
        <div className="row mt-3">

          {/* Today's Appointments */}
          <div className="col-xl-7 mb-3">
            <div className="card mb-0">
              <div className="card-header d-flex justify-content-between align-items-center py-3">
                <h5 className="card-title mb-0">Today&apos;s Appointments</h5>
                <Link to="/appointments" className="btn btn-sm btn-outline-primary">View All</Link>
              </div>
              <div className="card-body p-0">
                {todayAppts == null ? (
                  <div className="text-center py-4 text-muted">
                    <span className="spinner-border spinner-border-sm me-2" />Loading…
                  </div>
                ) : todayAppts.length === 0 ? (
                  <div className="text-center py-4 text-muted">No appointments today</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>Token</th>
                          <th>Patient</th>
                          <th>Doctor</th>
                          <th>Procedure</th>
                          <th style={{ width: 100 }}>Time</th>
                          <th style={{ width: 100 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayAppts.map((a, i) => (
                          <tr key={a.appointmentID ?? a.id ?? i}>
                            <td className="text-center">
                              <span className="badge bg-secondary">{a.tokenNumber ?? "—"}</span>
                            </td>
                            <td>{a.patientName ?? "—"}</td>
                            <td>{a.doctorName ?? "—"}</td>
                            <td>{a.procedureName ?? "—"}</td>
                            <td>{formatTime12(a.fromTime)}</td>
                            <td>
                              <span className={`badge bg-${STATUS_COLOR[a.status] ?? "secondary"}`}>
                                {a.status === "NoShow" ? "No Show" : (a.status ?? "—")}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Low Stock */}
          <div className="col-xl-5 mb-3">
            <div className="card mb-0">
              <div className="card-header d-flex justify-content-between align-items-center py-3">
                <h5 className="card-title mb-0">
                  Low Stock Alert
                  {lowStock?.length > 0 && (
                    <span className="badge bg-danger ms-2">{lowStock.length}</span>
                  )}
                </h5>
                <Link to="/stock" className="btn btn-sm btn-outline-danger">View Stock</Link>
              </div>
              <div className="card-body p-0">
                {lowStock == null ? (
                  <div className="text-center py-4 text-muted">
                    <span className="spinner-border spinner-border-sm me-2" />Loading…
                  </div>
                ) : lowStock.length === 0 ? (
                  <div className="text-center py-4 text-success">
                    <Icon.CheckCircle size={18} className="me-1" />All stocks adequate
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className="text-center" style={{ width: 90 }}>Current</th>
                          <th className="text-center" style={{ width: 90 }}>Min Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lowStock.map((item, i) => (
                          <tr key={item.productID ?? item.id ?? i}>
                            <td>{item.productName ?? item.name ?? "—"}</td>
                            <td className="text-center text-danger fw-bold">
                              {item.currentStock ?? item.currentQty ?? item.quantity ?? "—"}
                            </td>
                            <td className="text-center text-muted">
                              {item.minStock ?? item.reorderLevel ?? item.minimumStock ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
